import * as React from 'react';
import { t, type MessageKey } from '@reos/i18n';
import type { R3FixtureFact } from '@reos/duediligence';
import { Card } from './CaseUiPrimitives.js';
import { DateField } from './date-field.js';
import { IcoAresField } from './ico-ares-field.js';
import { canSeeMenuItem } from '../lib/capabilities.js';
import { useSession } from '../stores/session.js';
import { DomainApiError } from '../lib/api-error.js';
import { recordOwnerIdentity, type OwnerIdentityBody } from '../lib/human-tasks-api.js';

/**
 * Krok QAFIX3 bod 1 (nalez N05 QA 20. 10. 2026) — KARTA „VLASTNÍK — IDENTITA".
 *
 * `POST /v1/duediligence/cases/:id/owner-identity` existuje od kroku P1REP3
 * a je to JEDINA cesta, jak u pozemku a domu zacit lustraci v ISIR a CEE
 * (katastralni vlastnik je po KN4 z odpovedi vypnuty). Zadna obrazovka ho ale
 * nevolala — identitu slo zapsat jen pres API, takze report tiskl „ověříme
 * v insolvenčním rejstříku před podpisem" a lustraci delal clovek.
 *
 * Co karta dela a co NE:
 *  - zapisuje jmeno + datum narozeni (fyzicka osoba), nebo IČO z ARES
 *    (pravnicka osoba), podil a priznak „podepisuje"; vic vlastniku pres
 *    „Další vlastník",
 *  - RODNE CISLO POLE NEMA (tyz vzor jako `UnitCadastreForm`, zadani 15f):
 *    do dotazu smi jen se souhlasem nositele, a ten vznika v klientskem
 *    formulari. Server to navic odmita (`containsBirthNumber`),
 *  - lustraci NESPOUSTI — po zapisu to rekne („Lustrace ISIR/CEE se spustí
 *    sama"), dotazy dela dalsi beh kolektoru,
 *  - vysledek NEPOCITA: cte hotove fakty behu (`…owner_insolvency`,
 *    `…owner_execution`).
 *
 * CITLIVY UDAJ: formular se vykresli jen tomu, kdo ma `dd.fact.record` —
 * TENTYZ grant, kterym se autorizuje endpoint (`toAuthorized` v
 * `dd-human-tasks.ts`), takze obrazovka neslibuje, co by server odmitl.
 * V SEZNAMU uz zapsanych vlastniku je datum narozeni MASKOVANE (jen rok):
 * seznam cte kazdy, kdo zakazku vidi, a cele datum narozeni v nem nema co
 * delat.
 */

const cs = { locale: 'cs' } as const;

function tr(key: string, values?: Record<string, unknown>): string {
  return t(key as MessageKey, values === undefined ? cs : { ...cs, values });
}

/**
 * Fakty, ve kterych zije VYSLEDEK lustrace. Klice ziji v `@reos/duediligence`
 * (`INSOLVENCY_FACT_KEY`/`EXECUTION_FACT_KEY` pro pozemek a dum,
 * `unit_legal.*` pro byt) — hodnotovy import z barrelu kontextu by ale do
 * bundlu adminu pritahl `node:async_hooks` (poznani kroku REPORTS1), proto
 * se tu porovnava SUFIX, ktery maji oba oddily spolecny. Pridat treti oddil
 * znamena pridat jen jeho prefix u faktu, nic tady.
 */
const INSOLVENCY_SUFFIX = '.owner_insolvency';
const EXECUTION_SUFFIX = '.owner_execution';
/** Fakt, ktery vznikne tim zapisem (`OWNER_IDENTITY_FACT_KEY` v P1REP3). */
const OWNER_IDENTITY_SUFFIX = '.owner_identity';

/**
 * Polarita faktu („zjistilo se to?"). Fakt kolektoru nese hodnotu bud primo
 * (`true`/`false`), nebo v obalce `{ value, … }` — tyz tvar cte report
 * (`factValueSummary` v `dd-facts.ts`), proto se tu nic nedopocitava.
 */
function factPolarity(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (value !== null && typeof value === 'object') return (value as { value?: unknown }).value === true;
  return false;
}

interface OwnerRow {
  readonly personKind: 'NATURAL' | 'LEGAL';
  readonly surname: string;
  readonly firstName: string;
  readonly birthDate: string;
  readonly ico: string;
  /** Nazev z ARES — jen POTVRZENI pro cloveka, do API se neposila. */
  readonly companyName: string;
  readonly share: string;
  readonly signs: boolean;
}

const EMPTY_ROW: OwnerRow = {
  personKind: 'NATURAL',
  surname: '',
  firstName: '',
  birthDate: '',
  ico: '',
  companyName: '',
  share: '',
  signs: false,
};

/** Vlastnik, ze ktereho vznikne dotaz: prijmeni (osoba), nebo IČO (firma). */
function hasKey(row: OwnerRow): boolean {
  return row.personKind === 'LEGAL' ? row.ico.trim() !== '' : row.surname.trim() !== '';
}

function toBodyOwner(row: OwnerRow): OwnerIdentityBody['owners'][number] {
  const share = row.share.trim();
  if (row.personKind === 'LEGAL') {
    return {
      personKind: 'LEGAL',
      ico: row.ico.trim(),
      ...(share === '' ? {} : { share }),
      ...(row.signs ? { signs: true } : {}),
    };
  }
  const firstName = row.firstName.trim();
  const birthDate = row.birthDate.trim();
  return {
    personKind: 'NATURAL',
    surname: row.surname.trim(),
    ...(firstName === '' ? {} : { firstName }),
    ...(birthDate === '' ? {} : { birthDate }),
    ...(share === '' ? {} : { share }),
    ...(row.signs ? { signs: true } : {}),
  };
}

/**
 * Maskovane datum narozeni do seznamu: `1979-04-02` → `1979`. Nic jineho
 * z nej na obrazovku nejde (zadny den ani mesic).
 */
function maskBirthDate(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const year = /^(\d{4})-\d{2}-\d{2}$/.exec(raw);
  return year === null ? null : year[1] ?? null;
}

/** Vlastnici, kteri uz u zakazky zapsani jsou (hodnota faktu z P1REP3). */
function recordedOwners(fact: R3FixtureFact | undefined): readonly {
  readonly label: string;
  readonly birthYear: string | null;
}[] {
  const value = fact?.value;
  if (value === null || typeof value !== 'object') return [];
  const owners = (value as { owners?: unknown }).owners;
  if (!Array.isArray(owners)) return [];
  return owners.map((owner) => {
    const row = (owner ?? {}) as Record<string, unknown>;
    const ico = typeof row['ico'] === 'string' ? row['ico'] : null;
    const surname = typeof row['surname'] === 'string' ? row['surname'] : null;
    const firstName = typeof row['first_name'] === 'string' ? row['first_name'] : null;
    const label =
      ico !== null
        ? tr('webAdmin.ownerIdentity.recordedCompany', { ico })
        : [surname, firstName].filter((part) => part !== null && part !== '').join(' ');
    return {
      label: label === '' ? tr('webAdmin.ownerIdentity.recordedUnnamed') : label,
      birthYear: maskBirthDate(row['birth_date']),
    };
  });
}

export function OwnerIdentityCard({
  ddCaseId,
  facts,
}: {
  /** `dd_cases.id` — makler ho nikdy nevidi, dohleda ho `loadCaseDetail`. */
  ddCaseId: string;
  facts: readonly R3FixtureFact[];
}): React.JSX.Element {
  /**
   * Selektor vraci CELEHO uzivatele, ne `roles ?? []`: nove pole v kazdem
   * vykresleni je nova reference, a `useSyncExternalStore` z toho v detailu
   * zakazky udela nekonecnou smycku („Maximum update depth exceeded").
   */
  const user = useSession((s) => s.user);
  const roles = user?.roles ?? [];
  /** Tyz grant, kterym se autorizuje endpoint — ne vlastni vycet roli. */
  const canRecord = canSeeMenuItem(roles, 'dd.fact.record');
  const [rows, setRows] = React.useState<readonly OwnerRow[]>([EMPTY_ROW]);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState<{ owners: number; machineQueryReady: boolean } | null>(
    null,
  );

  const recorded = recordedOwners(facts.find((fact) => fact.factKey.endsWith(OWNER_IDENTITY_SUFFIX)));
  const lustration = facts.filter(
    (fact) =>
      fact.factKey.endsWith(INSOLVENCY_SUFFIX) || fact.factKey.endsWith(EXECUTION_SUFFIX),
  );

  const set = (index: number, patch: Partial<OwnerRow>): void => {
    setRows((previous) =>
      previous.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    );
  };

  const ready = rows.some(hasKey);

  const submit = (event: React.FormEvent): void => {
    event.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    setSaved(null);
    recordOwnerIdentity(ddCaseId, {
      owners: rows.filter(hasKey).map(toBodyOwner),
      /** Zapis maklere v adminu; `BUYER` patri klientskemu formulari. */
      enteredBy: 'BROKER',
    }).then(
      (result) => {
        setSaved({ owners: result.subjects, machineQueryReady: result.machineQueryReady });
        setBusy(false);
      },
      (cause: unknown) => {
        setError(
          cause instanceof DomainApiError
            ? cause.message
            : cause instanceof Error
              ? cause.message
              : String(cause),
        );
        setBusy(false);
      },
    );
  };

  return (
    <Card
      heading={tr('webAdmin.ownerIdentity.heading')}
      hint={tr('webAdmin.ownerIdentity.hint')}
      testId="owner-identity"
    >
      {recorded.length > 0 && (
        <ul
          style={{ margin: '0 0 12px 18px', fontSize: 'var(--rh-size-meta)' }}
          data-testid="owner-identity-recorded"
        >
          {recorded.map((owner, index) => (
            <li key={`${owner.label}-${index}`}>
              {owner.birthYear === null
                ? owner.label
                : tr('webAdmin.ownerIdentity.recordedWithYear', {
                    owner: owner.label,
                    year: owner.birthYear,
                  })}
            </li>
          ))}
        </ul>
      )}

      {canRecord ? (
        <form onSubmit={submit} data-testid="owner-identity-form">
          {rows.map((row, index) => (
            <fieldset
              key={index}
              style={{ border: '1px solid var(--rh-rule)', borderRadius: 6, padding: 10, marginBottom: 10 }}
              data-testid={`owner-identity-row-${index}`}
            >
              <label style={{ display: 'grid', gap: 2, fontSize: 'var(--rh-size-meta)', marginBottom: 8 }}>
                {tr('webAdmin.ownerIdentity.personKind')}
                <select
                  data-testid={`owner-identity-kind-${index}`}
                  value={row.personKind}
                  onChange={(event) =>
                    set(index, { personKind: event.target.value === 'LEGAL' ? 'LEGAL' : 'NATURAL' })
                  }
                >
                  <option value="NATURAL">{tr('webAdmin.ownerIdentity.kindNatural')}</option>
                  <option value="LEGAL">{tr('webAdmin.ownerIdentity.kindLegal')}</option>
                </select>
              </label>

              {row.personKind === 'NATURAL' ? (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: 10,
                  }}
                >
                  <label style={{ display: 'grid', gap: 2, fontSize: 'var(--rh-size-meta)' }}>
                    {tr('webAdmin.ownerIdentity.surname')}
                    <input
                      data-testid={`owner-identity-surname-${index}`}
                      value={row.surname}
                      onChange={(event) => set(index, { surname: event.target.value })}
                    />
                  </label>
                  <label style={{ display: 'grid', gap: 2, fontSize: 'var(--rh-size-meta)' }}>
                    {tr('webAdmin.ownerIdentity.firstName')}
                    <input
                      data-testid={`owner-identity-first-name-${index}`}
                      value={row.firstName}
                      onChange={(event) => set(index, { firstName: event.target.value })}
                    />
                  </label>
                  <label style={{ display: 'grid', gap: 2, fontSize: 'var(--rh-size-meta)' }}>
                    {tr('webAdmin.ownerIdentity.birthDate')}
                    {/*
                      `DateField` (ne nativni `type="date"`): Chromium kresli
                      nativni pole podle lokalizace OS, ne podle jazyka
                      aplikace — hlida to `test/datum-cs-hlidac.spec.ts`.
                      Hodnota je dal strojova `YYYY-MM-DD`, tedy presne to,
                      co ceka schema endpointu.
                    */}
                    <DateField
                      testId={`owner-identity-birth-date-${index}`}
                      value={row.birthDate}
                      onValueChange={(value) => set(index, { birthDate: value })}
                    />
                  </label>
                </div>
              ) : (
                /* Firma: IČO se nacita z ARES tymze polem jako u Nove faktury
                   a Noveho klienta (`IcoAresField`, krok UXK2) — nazev firmy
                   se NEPOSILA, dotaz do ISIR stoji na IČO. */
                <IcoAresField
                  id={`owner-identity-ico-${index}`}
                  label={tr('webAdmin.ownerIdentity.ico')}
                  value={row.ico}
                  onChange={(value) => set(index, { ico: value })}
                  onFound={(company) => set(index, { ico: company.ico, companyName: company.companyName })}
                />
              )}
              {row.personKind === 'LEGAL' && row.companyName !== '' && (
                <p
                  style={{ margin: '4px 0 0', fontSize: 'var(--rh-size-meta)' }}
                  data-testid={`owner-identity-company-${index}`}
                >
                  {row.companyName}
                </p>
              )}

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: 10,
                  marginTop: 10,
                }}
              >
                <label style={{ display: 'grid', gap: 2, fontSize: 'var(--rh-size-meta)' }}>
                  {tr('webAdmin.ownerIdentity.share')}
                  <input
                    data-testid={`owner-identity-share-${index}`}
                    placeholder="1/1"
                    value={row.share}
                    onChange={(event) => set(index, { share: event.target.value })}
                  />
                </label>
                <label style={{ alignSelf: 'end', fontSize: 'var(--rh-size-meta)' }}>
                  <input
                    type="checkbox"
                    data-testid={`owner-identity-signs-${index}`}
                    checked={row.signs}
                    onChange={(event) => set(index, { signs: event.target.checked })}
                  />{' '}
                  {tr('webAdmin.ownerIdentity.signs')}
                </label>
              </div>
            </fieldset>
          ))}

          <button
            type="button"
            className="rh-a2-btn"
            data-testid="owner-identity-add"
            onClick={() => setRows((previous) => [...previous, EMPTY_ROW])}
          >
            {tr('webAdmin.ownerIdentity.addOwner')}
          </button>{' '}
          <button type="submit" disabled={busy || !ready} data-testid="owner-identity-save">
            {tr('webAdmin.ownerIdentity.save')}
          </button>
        </form>
      ) : (
        <p style={{ margin: 0, fontSize: 'var(--rh-size-meta)', color: 'var(--rh-text-muted)' }} data-testid="owner-identity-readonly">
          {tr('webAdmin.ownerIdentity.noPermission')}
        </p>
      )}

      {saved !== null && (
        <p role="status" style={{ marginTop: 8, fontSize: 'var(--rh-size-meta)' }} data-testid="owner-identity-saved">
          {saved.machineQueryReady
            ? tr('webAdmin.ownerIdentity.savedAuto', { count: String(saved.owners) })
            : tr('webAdmin.ownerIdentity.savedHuman', { count: String(saved.owners) })}
        </p>
      )}

      {lustration.length > 0 && (
        <ul
          style={{ margin: '8px 0 0 18px', fontSize: 'var(--rh-size-meta)' }}
          data-testid="owner-identity-lustration"
        >
          {lustration.map((fact) => (
            <li key={fact.id} data-fact-key={fact.factKey}>
              {tr(
                fact.factKey.endsWith(INSOLVENCY_SUFFIX)
                  ? 'webAdmin.ownerIdentity.lustrationInsolvency'
                  : 'webAdmin.ownerIdentity.lustrationExecution',
                { status: tr(`webAdmin.ownerIdentity.answer.${factPolarity(fact.value) ? 'yes' : 'no'}`) },
              )}
            </li>
          ))}
        </ul>
      )}

      {error !== null && (
        <p role="alert" style={{ marginTop: 8, fontSize: 'var(--rh-size-meta)', color: 'var(--rh-danger)' }} data-testid="owner-identity-error">
          {error}
        </p>
      )}
    </Card>
  );
}
